import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Home, Search, ClipboardList, MessageCircle, User } from 'lucide-react';

const HIDDEN_ON = ['/login', '/role-select', '/verify-mobile', '/auth/callback'];

const NAV_ITEMS = [
  { label: 'Home',    icon: Home,          path: '/' },
  { label: 'Search',  icon: Search,        path: '/search' },
  { label: 'Post Req',icon: ClipboardList, path: '/post-requirement', accent: true },
  { label: 'Messages',icon: MessageCircle, path: '/messages', authRequired: true },
  { label: 'Profile', icon: User,          path: '/dashboard', authRequired: true, guestPath: '/login' },
];

export default function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Add body padding so content isn't hidden behind the nav bar
  useEffect(() => {
    document.body.style.paddingBottom = 'calc(64px + env(safe-area-inset-bottom, 0px))';
    return () => { document.body.style.paddingBottom = ''; };
  }, []);

  if (HIDDEN_ON.some(p => location.pathname.startsWith(p))) return null;

  const isActive = (path) => path === '/'
    ? location.pathname === '/'
    : location.pathname.startsWith(path);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-slate-200 shadow-2xl shadow-slate-900/10"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-center justify-around h-16">
        {NAV_ITEMS.map(({ label, icon: Icon, path, accent, authRequired, guestPath }) => {
          const active = isActive(path);
          const destination = authRequired && !user ? (guestPath || '/login') : path;

          return (
            <button
              key={label}
              onClick={() => navigate(destination)}
              className={`flex flex-col items-center justify-center flex-1 h-full gap-0.5 relative transition-colors
                ${active
                  ? accent ? 'text-green-600' : 'text-blue-700'
                  : 'text-slate-400 hover:text-slate-600'
                }`}
              data-testid={`bottom-nav-${label.toLowerCase().replace(' ', '-')}`}
            >
              {/* Active indicator dot */}
              {active && (
                <span className={`absolute top-1.5 w-1 h-1 rounded-full ${accent ? 'bg-green-600' : 'bg-blue-700'}`} />
              )}

              {/* Icon with accent background for Post Req */}
              {accent ? (
                <div className={`w-10 h-7 rounded-xl flex items-center justify-center transition-all
                  ${active ? 'bg-green-600' : 'bg-green-50 border border-green-200'}`}>
                  <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-green-600'}`} />
                </div>
              ) : (
                <Icon className="w-5 h-5" />
              )}

              <span className="text-[10px] font-semibold leading-none">{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
